import { z } from 'zod';
import { IdSchema } from './common.js';

// ----- Centro de informes (Etapa 3) -----
export const ReportQuerySchema = z.object({
  from: z.string().date().optional(),
  to: z.string().date().optional(),
  all: z.enum(['true', 'false']).default('false').transform((v) => v === 'true'),
  location_id: IdSchema.optional(),
  product_id: IdSchema.optional(),
  brand: z.string().trim().max(60).optional(),
  category: z.string().trim().max(60).optional(),
  user_id: IdSchema.optional(),
  limit: z.coerce.number().int().min(1).max(5000).default(500),
  offset: z.coerce.number().int().min(0).default(0),
});

/** Filtros aplicados, para poder imprimirlos en el encabezado del informe. */
export const ReportFiltersSchema = z.object({
  location_id: z.number().int().nullable(),
  location: z.string().nullable(),
  product_id: z.number().int().nullable(),
  product: z.string().nullable(),
  brand: z.string().nullable(),
  category: z.string().nullable(),
  user_id: z.number().int().nullable(),
  user: z.string().nullable(),
});

const Base = { from: z.string(), to: z.string(), days: z.number().int(), filters: ReportFiltersSchema, total_rows: z.number().int() };

export const SalesReportSchema = z.object({
  ...Base,
  totals: z.object({
    lines: z.number().int(), units: z.number().int(), sales: z.number(), real_cost: z.number(), profit: z.number(),
    margin_pct: z.number(), suggested: z.number(), discount: z.number(), negotiated_lines: z.number().int(),
  }),
  rows: z.array(z.object({
    line_id: z.number().int(), day: z.string(), doc_no: z.string(), created_at: z.string(),
    location_code: z.string(), location_name: z.string(), user_name: z.string().nullable(),
    ref: z.string(), product_name: z.string(), brand: z.string(), category: z.string(),
    color: z.string().nullable(), size: z.string().nullable(), qty: z.number().int(),
    suggested_price: z.number(), unit_price: z.number(), negotiated: z.boolean(), landed_cost: z.number(),
    sales: z.number(), real_cost: z.number(), profit: z.number(), discount: z.number(),
  })),
});
export type SalesReport = z.infer<typeof SalesReportSchema>;

export const ProductsReportSchema = z.object({
  ...Base,
  totals: z.object({ products: z.number().int(), units: z.number().int(), sales: z.number(), real_cost: z.number(), profit: z.number(), discount: z.number(), margin_pct: z.number() }),
  rows: z.array(z.object({
    product_id: z.number().int(), ref: z.string(), product_name: z.string(), brand: z.string(), category: z.string(),
    units: z.number().int(), sales: z.number(), real_cost: z.number(), profit: z.number(), margin_pct: z.number(),
    suggested: z.number(), discount: z.number(), avg_suggested: z.number(), avg_sold: z.number(),
  })),
});
export type ProductsReport = z.infer<typeof ProductsReportSchema>;

export const SummaryReportSchema = z.object({
  from: z.string(), to: z.string(), days: z.number().int(), filters: ReportFiltersSchema,
  /** Ventas a clientes: son las únicas que suman ingresos y utilidad de la empresa. */
  external: z.object({ units: z.number().int(), sales: z.number(), real_cost: z.number(), profit: z.number(), margin_pct: z.number(), store: z.number(), direct: z.number() }),
  locations: z.array(z.object({
    location_id: z.number().int(), code: z.string(), name: z.string(), type: z.enum(['WAREHOUSE', 'STORE']),
    units: z.number().int(), sales: z.number(), internal_cost: z.number(), gross_margin: z.number(),
    external_sales: z.number().default(0), internal_sales: z.number().default(0),
    real_cost: z.number(), company_profit: z.number(), fixed_prorated: z.number(), result: z.number(),
  })),
  /** Traslados bodega → locales: se informan aparte, no suman ingresos ni ganancias. */
  internal: z.object({ count: z.number().int(), total: z.number() }),
  fixed_total: z.number(),
  company_result: z.number(),
});
export type SummaryReport = z.infer<typeof SummaryReportSchema>;
